import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { verifyMeter, buyElectricity } from "../../api/apiServices";
import { useAuth } from "../../context/useAuth";

const DISCOS = [
  { id: "EKEDC", label: "Eko Electric", short: "EKEDC", color: "#D97706" },
  { id: "IKEDC", label: "Ikeja Electric", short: "IKEDC", color: "#DC2626" },
  { id: "AEDC", label: "Abuja Electric", short: "AEDC", color: "#059669" },
  { id: "PHEDC", label: "Port Harcourt", short: "PHEDC", color: "#0284C7" },
  { id: "IBEDC", label: "Ibadan Electric", short: "IBEDC", color: "#7C3AED" },
  { id: "EEDC", label: "Enugu Electric", short: "EEDC", color: "#0F766E" },
  { id: "KAEDCO", label: "Kaduna Electric", short: "KAEDCO", color: "#B45309" },
  { id: "KEDCO", label: "Kano Electric", short: "KEDCO", color: "#065F46" },
];

const QUICK_AMOUNTS = [1000, 2000, 5000, 10000, 20000, 50000];

export default function ElectricityPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  const [disco, setDisco] = useState(searchParams.get("disco") || "");
  const [meterType, setMeterType] = useState("prepaid");
  const [meterNumber, setMeterNumber] = useState("");
  const [amount, setAmount] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [meterInfo, setMeterInfo] = useState(null); // { customerName, address }
  const [loading, setLoading] = useState(false);

  const isValid = disco && meterNumber.length >= 10 && Number(amount) >= 1000 && !!meterInfo;

  const handleVerifyMeter = async () => {
    if (!disco || meterNumber.length < 10) return;
    setVerifying(true);
    setMeterInfo(null);
    try {
      const res = await verifyMeter(disco, meterNumber, meterType);
      if (res.status) {
        setMeterInfo({ customerName: res.customerName, address: res.address });
      } else {
        toast.error(res.message || "Meter not found");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Meter verification failed");
    } finally {
      setVerifying(false);
    }
  };

  const num = "07014970207"

  const handlePay = async () => {
    if (!isValid || loading) return;
    setLoading(true);
    try {
      const res = await buyElectricity(
        disco,
        meterNumber,
        meterType,
        Number(amount),
        // user?.phone
        Number(num)
      );

      if (res.status) {
        navigate("/bills/success", {
          state: {
            type: "electricity",
            title: `${disco} ${meterType === "prepaid" ? "Prepaid" : "Postpaid"}`,
            detail: `Meter: ${meterNumber}`,
            subDetail: meterInfo.customerName,
            amount: Number(amount),
            requestId: res.requestId,
            transactionId: res.transactionId,
            // Show token to user for prepaid meters
            token: res.token || null,
          },
        });
      } else {
        toast.error(res.message || "Electricity purchase failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Electricity purchase failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F8FA] pb-10">
      {/* Header */}
      <div className="bg-white px-5 pt-12 pb-5 border-b border-slate-100">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/bills")} className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center">
            <i className="ri-arrow-left-line text-slate-600 text-lg" />
          </button>
          <div>
            <h1 className="text-slate-800 font-bold text-lg" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Electricity</h1>
            <p className="text-slate-400 text-xs">Pay any DISCO across Nigeria</p>
          </div>
        </div>
      </div>

      <div className="px-5 pt-6 space-y-5">
        {/* Meter type toggle */}
        <div className="flex items-center gap-2 bg-slate-100 rounded-2xl p-1">
          {["prepaid", "postpaid"].map((type) => (
            <button
              key={type}
              onClick={() => { setMeterType(type); setMeterInfo(null); }}
              className={`flex-1 py-3 rounded-xl text-sm font-semibold capitalize transition-all ${
                meterType === type ? "bg-white text-emerald-600 shadow-sm" : "text-slate-400"
              }`}
            >
              {type}
            </button>
          ))}
        </div>

        {/* DISCO */}
        <div>
          <p className="text-slate-600 text-sm font-semibold mb-3">Select DISCO</p>
          <div className="grid grid-cols-2 gap-2">
            {DISCOS.map((d) => (
              <button
                key={d.id}
                onClick={() => { setDisco(d.id); setMeterInfo(null); }}
                className={`flex items-center gap-3 p-3 rounded-2xl border-2 transition-all text-left ${
                  disco === d.id ? "border-emerald-500 bg-emerald-50" : "border-slate-100 bg-white"
                }`}
              >
                <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: d.color + "18" }}>
                  <i className="ri-flashlight-line text-base" style={{ color: d.color }} />
                </div>
                <div>
                  <p className="text-slate-800 text-xs font-bold">{d.short}</p>
                  <p className="text-slate-400 text-[10px] leading-tight">{d.label}</p>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Meter Number */}
        <div>
          <p className="text-slate-600 text-sm font-semibold mb-3">Meter Number</p>
          <div className="bg-white border-2 border-slate-200 rounded-2xl flex items-center px-4 focus-within:border-emerald-400 transition-colors">
            <i className="ri-flashlight-line text-slate-400 mr-3" />
            <input
              type="text"
              value={meterNumber}
              onChange={(e) => {
                setMeterNumber(e.target.value.replace(/\D/g, "").slice(0, 13));
                setMeterInfo(null);
              }}
              onBlur={handleVerifyMeter}
              placeholder="Enter meter number"
              className="flex-1 py-4 outline-none text-slate-800 text-sm bg-transparent placeholder-slate-300"
            />
            {verifying && <div className="w-4 h-4 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />}
            {meterInfo && !verifying && <i className="ri-checkbox-circle-fill text-emerald-500 text-lg" />}
          </div>
          {meterInfo && (
            <div className="mt-2 px-4 py-2.5 bg-emerald-50 rounded-xl border border-emerald-200">
              <p className="text-emerald-700 text-xs font-bold">{meterInfo.customerName}</p>
              {meterInfo.address && <p className="text-emerald-600 text-xs mt-0.5">{meterInfo.address}</p>}
            </div>
          )}
          {/* Show hint if disco not selected when user tries to blur */}
          {!disco && meterNumber.length > 3 && (
            <p className="text-amber-500 text-xs mt-2 px-1">⚠ Please select a DISCO first</p>
          )}
        </div>

        {/* Amount */}
        <div>
          <p className="text-slate-600 text-sm font-semibold mb-3">Amount (min ₦1,000)</p>
          <div className="bg-white border-2 border-slate-200 rounded-2xl flex items-center px-4 focus-within:border-emerald-400 transition-colors mb-3">
            <span className="text-slate-400 font-bold mr-2">₦</span>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              className="flex-1 py-4 outline-none text-slate-800 text-sm bg-transparent placeholder-slate-300"
            />
          </div>
          <div className="grid grid-cols-3 gap-2">
            {QUICK_AMOUNTS.map((amt) => (
              <button
                key={amt}
                onClick={() => setAmount(String(amt))}
                className={`py-2.5 rounded-xl text-sm font-semibold border transition-all ${
                  amount === String(amt)
                    ? "bg-emerald-600 text-white border-emerald-600"
                    : "bg-white text-slate-600 border-slate-200 hover:border-emerald-300"
                }`}
              >
                ₦{amt >= 1000 ? `${amt / 1000}k` : amt}
              </button>
            ))}
          </div>
        </div>

        <button
          onClick={handlePay}
          disabled={!isValid || loading}
          className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-200 disabled:text-slate-400 text-white py-4 rounded-2xl font-bold text-sm transition-all active:scale-95 flex items-center justify-center gap-2"
        >
          {loading ? (
            <><div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />Processing…</>
          ) : isValid ? `Pay ₦${Number(amount).toLocaleString()}` : "Fill in details to continue"}
        </button>
      </div>
    </div>
  );
}
